import { foldDoors } from "../document/doors.ts";
import { hostIssueOf } from "../document/host-issues.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type EntityInstance, type MapDocument, type WorldDocument } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";

/**
 * The links of doors, rooms' hosts and arrival points (ADR-122 decisions 2.4 to 2.6; its note "how P1.4-T138 is
 * built", items 1, 2 and 7): what is broken on one map, what the publish refuses over a world, and what leads to a
 * map or an arrival point, which a delete names before it runs. One check serves three stages: the Issues list
 * shows each link issue as a warning, the compile refuses a ready map's and warns of a draft's, and the publish
 * refuses them all; a one-way door is an ignorable info everywhere and refuses nothing. Daedalus holds no notion
 * of walking, so a blocked cell is whatever the caller's collision says it is.
 */

/**
 * What the link checks read besides the world: the entity type the game treats as an arrival point (any entity of
 * a door's or a host's arrival id counts without one, and no marker is checked as an arrival point), and the
 * collision, `blocked` answering whether a collision cell of a map holds a byte other than 0 (no cell is checked
 * without it).
 */
export interface LinkScope {
  readonly arrivalType?: string;
  readonly blocked?: (map: MapDocument, column: number, row: number) => boolean;
}

/**
 * A door that leads somewhere: the map holding it, its layer, its id, its name and the arrival point it names.
 */
export interface DoorWay {
  readonly mapId: string;
  readonly layerId: string;
  readonly doorId: string;
  readonly name: string;
  readonly arrival: string;
}

/**
 * A room whose host is a map's arrival point: the room and the arrival point.
 */
export interface HostWay {
  readonly mapId: string;
  readonly arrival: string;
}

/**
 * What leads to a map or to one of its arrival points.
 */
export interface Ways {
  readonly doors: readonly DoorWay[];
  readonly hosts: readonly HostWay[];
}

/**
 * Every door of a map, layer by layer in document order, with its layer's id.
 *
 * @param map The map.
 * @returns The doors.
 */
function doorsOf(map: MapDocument): { readonly layerId: string; readonly door: DoorDefinition }[] {
  return map.layers.flatMap((layer) => (layer.kind === "door" ? layer.doors.map((door) => ({ layerId: layer.id, door })) : []));
}

/**
 * The name an arrival point goes by in words: its `name` field when it holds text, else its id.
 *
 * @param entity The arrival point.
 * @param entity.id Its id.
 * @param entity.fields Its fields.
 * @returns The name.
 */
export function arrivalName(entity: { readonly id: string; readonly fields: { readonly [key: string]: unknown } }): string {
  const named = entity.fields["name"];
  return typeof named === "string" && named.trim() !== "" ? named : entity.id;
}

/**
 * Every arrival point of a map with its layer's id: the entities of the arrival type, none without one.
 *
 * @param map The map.
 * @param arrivalType The game's arrival type.
 * @returns The arrival points, in layer and placement order.
 */
function arrivalsOf(map: MapDocument, arrivalType: string | undefined): { readonly layerId: string; readonly entity: EntityInstance }[] {
  if (arrivalType === undefined) {
    return [];
  }
  return map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((entity) => entity.type === arrivalType).map((entity) => ({ layerId: layer.id, entity })) : []));
}

/**
 * Whether a map holds an arrival point of an id: an entity of the arrival type, or of any type without one.
 *
 * @param map The map.
 * @param id The arrival point's id.
 * @param arrivalType The game's arrival type.
 * @returns True when it does.
 */
function holdsArrival(map: MapDocument, id: string, arrivalType: string | undefined): boolean {
  return map.layers.some((layer) => layer.kind === "entity" && layer.entities.some((entity) => entity.id === id && (arrivalType === undefined || entity.type === arrivalType)));
}

/**
 * Whether any door of a map leads to another map.
 *
 * @param map The map holding the doors.
 * @param to The map they would lead to.
 * @returns True when one does.
 */
function leadsTo(map: MapDocument, to: string): boolean {
  return doorsOf(map).some(({ door }) => door.to.map === to);
}

/**
 * The issues of one map's doors: a destination the world lacks, an arrival point the destination does not hold,
 * and, for a door whose destination exists, one that holds no door back.
 *
 * @param world The world.
 * @param map The map.
 * @param arrivalType The game's arrival type.
 * @returns The issues, in door order.
 */
function doorIssuesOf(world: WorldDocument, map: MapDocument, arrivalType: string | undefined): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const { layerId, door } of doorsOf(map)) {
    const where = { mapId: map.id, layerId, doorId: door.id };
    const target = world.maps.find((candidate) => candidate.id === door.to.map);
    if (target === undefined) {
      issues.push({ ...where, kind: "door.missing-map", severity: "warning", message: `door ${door.name} leads to map ${door.to.map}, which the world lacks; choose another destination` });
      continue;
    }
    if (!holdsArrival(target, door.to.arrival, arrivalType)) {
      issues.push({ ...where, kind: "door.missing-arrival", severity: "warning", message: `door ${door.name} leads to an arrival point that ${target.name} does not hold; choose another arrival point` });
    }
    if (!leadsTo(target, map.id)) {
      issues.push({ ...where, kind: "door.one-way", severity: "info", ignorable: true, message: `door ${door.name} is one way: ${target.name} has no door back to ${map.name}; add a door back on it, or ignore this when it is meant` });
    }
  }
  return issues;
}

/**
 * The issues of one map's arrival points: one standing on a door cell, which would send an arrival straight back
 * through it, and one standing on a blocked cell.
 *
 * @param map The map.
 * @param scope The arrival type and the collision.
 * @returns The issues, in placement order.
 */
function arrivalIssuesOf(map: MapDocument, scope: LinkScope): ValidationIssue[] {
  const arrivals = arrivalsOf(map, scope.arrivalType);
  if (arrivals.length === 0) {
    return [];
  }
  const doors = map.layers.some((layer) => layer.kind === "door") ? foldDoors(map).cells : null;
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const height = map.height * COLLISION_CELLS_PER_TILE;
  const issues: ValidationIssue[] = [];
  for (const { layerId, entity } of arrivals) {
    const column = Math.floor(entity.x * COLLISION_CELLS_PER_TILE);
    const row = Math.floor(entity.y * COLLISION_CELLS_PER_TILE);
    const inside = column >= 0 && row >= 0 && column < width && row < height;
    const where = { mapId: map.id, layerId, entityId: entity.id };
    if (inside && doors !== null && doors[row * width + column] !== 0) {
      issues.push({ ...where, kind: "arrival.on-door", severity: "warning", message: `arrival point ${arrivalName(entity)} stands on a door, which would send an arrival straight back; move it off the door's cells` });
    } else if (inside && scope.blocked?.(map, column, row) === true) {
      issues.push({ ...where, kind: "arrival.blocked", severity: "warning", message: `arrival point ${arrivalName(entity)} stands on a blocked cell, where nobody can stand; move it onto open ground` });
    }
  }
  return issues;
}

/**
 * The link issues of one map: its doors', then its arrival points', then its host's, each a warning but the one-way
 * door, an ignorable info.
 *
 * @param world The world, for the doors' and the host's destinations.
 * @param map The map.
 * @param scope The arrival type and the collision.
 * @returns The issues.
 */
export function mapLinkIssues(world: WorldDocument, map: MapDocument, scope: LinkScope): ValidationIssue[] {
  const host = hostIssueOf(world, map, scope.arrivalType);
  return [...doorIssuesOf(world, map, scope.arrivalType), ...arrivalIssuesOf(map, scope), ...(host === null ? [] : [{ mapId: host.mapId, kind: host.kind, severity: "warning" as const, message: host.message }])];
}

/**
 * Whether a map is blank: it holds no layer (P1.4-T80's word).
 *
 * @param map The map.
 * @returns True when it is.
 */
function isBlank(map: MapDocument): boolean {
  return map.layers.length === 0;
}

/**
 * The doors of a ready map that lead to a draft or a blank map, which no player could go through.
 *
 * @param world The world.
 * @param map The map.
 * @returns One refusal per such door.
 */
function unfinishedDestinations(world: WorldDocument, map: MapDocument): ValidationIssue[] {
  if (map.draft === true) {
    return [];
  }
  return doorsOf(map).flatMap(({ layerId, door }): ValidationIssue[] => {
    const target = world.maps.find((candidate) => candidate.id === door.to.map);
    const where = { mapId: map.id, layerId, doorId: door.id };
    if (target?.draft === true) {
      return [{ ...where, kind: "door.to-draft", message: `door ${door.name} on ready map ${map.name} leads to draft map ${target.name}; mark ${target.name} ready or choose another destination` }];
    }
    if (target !== undefined && isBlank(target)) {
      return [{ ...where, kind: "door.to-blank", message: `door ${door.name} on ready map ${map.name} leads to map ${target.name}, which is blank; build ${target.name} or choose another destination` }];
    }
    return [];
  });
}

/**
 * What a publish refuses of a world's links, over every map it carries: each link issue but the one-way info, and
 * each ready map's door to a draft or a blank map, all as refusals.
 *
 * @param world The world as the version carries it.
 * @param scope The arrival type and the version's collision.
 * @param carried Whether the version carries a map's chunks (ADR-109 decision 9); only those maps are checked, since
 *   a draft or blank map left out has no collision to read. A ready map's door to one that is left out is still
 *   refused. Every map by default.
 * @returns The refusals, map by map in the world's order; empty when the publish may go on.
 */
export function publishLinkIssues(world: WorldDocument, scope: LinkScope, carried: (map: MapDocument) => boolean = () => true): ValidationIssue[] {
  return world.maps.filter(carried).flatMap((map) => [
    ...mapLinkIssues(world, map, scope)
      .filter((issue) => issue.severity !== "info")
      .map(({ severity: _severity, ...issue }) => issue),
    ...unfinishedDestinations(world, map),
  ]);
}

/**
 * What leads to a map, or to one of its arrival points: the doors naming it and the rooms hosted on it. For a whole
 * map, its own doors and itself are left out, since they go with it.
 *
 * @param world The world.
 * @param mapId The map.
 * @param arrivalId One of its arrival points, or undefined for any.
 * @returns The doors, map by map, layer by layer, in list order, and the hosts in the world's map order.
 */
export function waysTo(world: WorldDocument, mapId: string, arrivalId?: string): Ways {
  const names = (arrival: string): boolean => arrivalId === undefined || arrival === arrivalId;
  const others = world.maps.filter((map) => arrivalId !== undefined || map.id !== mapId);
  const doors = others.flatMap((map) => doorsOf(map).flatMap(({ layerId, door }) => (door.to.map === mapId && names(door.to.arrival) ? [{ mapId: map.id, layerId, doorId: door.id, name: door.name, arrival: door.to.arrival }] : [])));
  const hosts = others.flatMap((map) => (map.host !== undefined && map.host.map === mapId && names(map.host.arrival) ? [{ mapId: map.id, arrival: map.host.arrival }] : []));
  return { doors, hosts };
}

/**
 * A count with its noun.
 *
 * @param count The count.
 * @param noun The noun, singular.
 * @returns The words.
 */
function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * The words a delete's question says of what leads to what it removes ("2 doors and 1 room lead here").
 *
 * @param ways What leads there.
 * @returns The words, or null when nothing does.
 */
export function waysToWords(ways: Ways): string | null {
  const parts = [...(ways.doors.length === 0 ? [] : [counted(ways.doors.length, "door")]), ...(ways.hosts.length === 0 ? [] : [counted(ways.hosts.length, "room")])];
  if (parts.length === 0) {
    return null;
  }
  return `${parts.join(" and ")} ${ways.doors.length + ways.hosts.length === 1 ? "leads" : "lead"} here`;
}

/**
 * Whether a door's destination holds a door leading back to the door's map, so "Make the way back" need not be
 * offered.
 *
 * @param world The world.
 * @param mapId The door's map.
 * @param doorId The door.
 * @returns True when it does; false when it does not, or no such door or destination exists.
 */
export function hasWayBack(world: WorldDocument, mapId: string, doorId: string): boolean {
  const map = world.maps.find((candidate) => candidate.id === mapId);
  const door = map === undefined ? undefined : doorsOf(map).find((candidate) => candidate.door.id === doorId)?.door;
  const target = door === undefined ? undefined : world.maps.find((candidate) => candidate.id === door.to.map);
  return target !== undefined && leadsTo(target, mapId);
}
